/**
 * Theme and styles configuration
 * Warm, refined aesthetic with tight spacing
 */

export const colors = {
  primary: '#FF9440',
  primaryDark: '#E67A1E',
  primaryGradient: 'linear-gradient(180deg, #FF9440 0%, #FFFFFF 100%)',
  primaryLight: '#FFF6EF',
  primaryMuted: 'rgba(255, 148, 64, 0.1)',
  text: '#1A1A1A',
  textLight: '#4A4A4A',
  textMuted: '#7A7A7A',
  white: '#fff',
  cream: '#F7F5F2',
  background: '#FAFAF8',
  backgroundAlt: '#F2F0ED',
  border: '#E8E5E1',
  borderDark: '#D4D0CC',
  footer: '#1A1A1A',
  success: '#2D8B4E',
  error: '#D93025',
  // Per-language accents reused across the site. Used one-at-a-time today, but
  // they also have to hold up as a categorical set (a chart or legend showing
  // several languages at once), so they're kept CVD-validated:
  //   node scripts/validate_palette.js "<hex,…>" --mode light   (dataviz skill)
  // Punjabi was re-stepped off the Bengali red (dE 11.1 in normal vision) and
  // Marathi lifted above the chroma floor, where it read as gray.
  gujarati: '#FF9440',
  hindi: '#1FA46A',
  malayalam: '#1689F2',
  punjabi: '#7C3AED',
  bengali: '#B42318',
  marathi: '#0D9488',
};

export const fonts = {
  heading: "ui-rounded, 'SF Pro Rounded', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  body: "'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

// Shared character-led surfaces for Home and About. No font download or animation runtime.
export const playful = {
  orange: '#AE4B00',
  heading: { fontFamily: fonts.heading, fontWeight: 850, letterSpacing: '-0.025em', lineHeight: 1.12, margin: 0 },
  card: { border: '2px solid #E6E4DF', borderBottomWidth: 5, borderRadius: 26, background: '#fff' },
  smallButton: { font: 'inherit', fontWeight: 800, color: '#444', background: '#fff', border: '2px solid #DDDCD5', borderBottomWidth: 4, borderRadius: 14, minWidth: 44, minHeight: 44, padding: '8px 14px', cursor: 'pointer' },
};

// Shared layout + surface tokens. Keeping these in one place is what makes the
// inline-style approach feel consistent instead of ad hoc across pages.
export const layout = {
  maxWidth: 1100,
  // Fluid page gutter — shrinks on phones, breathes on desktop, no media query.
  pagePadX: 'clamp(1rem, 4vw, 1.5rem)',
};

export const radii = {
  sm: 10,
  md: 14,
  lg: 18,
  xl: 24,
  pill: 999,
};

export const shadow = {
  sm: '0 2px 8px rgba(72, 53, 34, 0.06)',
  md: '0 10px 30px rgba(72, 53, 34, 0.08)',
  lg: '0 22px 54px rgba(72, 53, 34, 0.10)',
  button: '0 6px 18px rgba(26, 26, 26, 0.16)',
};

export const styles = {
  container: {
    fontFamily: fonts.body,
    margin: 0,
    padding: 0,
    minHeight: '100vh',
    backgroundColor: colors.background,
    lineHeight: 1.6,
    color: colors.text,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  header: {
    background: colors.white,
    color: colors.text,
    padding: '0.6rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    marginTop: 0
  },
  nav: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 1.5rem',
    position: 'relative'
  },
  logo: {
    fontSize: '1.5rem',
    fontWeight: '700',
    fontFamily: fonts.heading,
    textDecoration: 'none',
    color: colors.text,
    zIndex: 1001,
    letterSpacing: 0,
  },
  hamburger: {
    display: 'none',
    flexDirection: 'column',
    cursor: 'pointer',
    zIndex: 1001,
    gap: '5px'
  },
  hamburgerLine: {
    width: '22px',
    height: '2px',
    backgroundColor: colors.text,
    transition: 'all 0.3s ease',
    borderRadius: '1px'
  },
  navLinks: {
    display: 'flex',
    gap: '2rem',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    alignItems: 'center'
  },
  navLinksOpen: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    width: '75%',
    maxWidth: '320px',
    background: colors.white,
    padding: '5rem 2rem 2rem',
    gap: '1.5rem',
    listStyle: 'none',
    margin: 0,
    boxShadow: '-8px 0 30px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s ease',
    zIndex: 1000
  },
  navLink: {
    color: colors.textLight,
    textDecoration: 'none',
    fontSize: '0.95rem',
    transition: 'color 0.2s',
    fontWeight: '500',
    cursor: 'pointer',
    letterSpacing: '0.01em',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 999,
    transition: 'opacity 0.3s ease'
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: 'clamp(1.25rem, 4vw, 2.25rem) clamp(1rem, 4vw, 1.5rem)'
  },
  hero: {
    textAlign: 'center',
    padding: 'clamp(2.25rem, 6vw, 3.75rem) clamp(1.25rem, 5vw, 2.5rem)',
    background: 'linear-gradient(170deg, #FFF1E5 0%, #FAF6F1 60%, #F7F5F2 100%)',
    borderRadius: 24,
    margin: '1rem 0 2rem',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 10px 30px rgba(72, 53, 34, 0.05)',
  },
  heroTitle: {
    fontSize: 'clamp(2.05rem, 6vw, 3.1rem)',
    fontWeight: '800',
    fontFamily: fonts.heading,
    color: colors.text,
    marginBottom: '1rem',
    lineHeight: 1.08,
    letterSpacing: '-0.02em'
  },
  heroSubtitle: {
    fontSize: 'clamp(1.02rem, 2.4vw, 1.18rem)',
    color: colors.textLight,
    marginBottom: '2rem',
    lineHeight: 1.6,
    maxWidth: '540px',
    margin: '0 auto 2rem'
  },
  button: {
    background: colors.text,
    color: 'white',
    border: 'none',
    padding: '13px 30px',
    fontSize: '0.95rem',
    fontWeight: '700',
    fontFamily: fonts.body,
    borderRadius: 12,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
    boxShadow: '0 6px 18px rgba(26, 26, 26, 0.16)',
    letterSpacing: '0.01em',
  },
  buttonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 26px rgba(26, 26, 26, 0.22)',
    background: '#000',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
    gap: '1.25rem',
    margin: '2rem 0'
  },
  card: {
    padding: 'clamp(1.4rem, 4vw, 2rem)',
    backgroundColor: colors.white,
    borderRadius: 18,
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    cursor: 'pointer'
  },
  cardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 18px 40px rgba(72, 53, 34, 0.10)',
    borderColor: colors.borderDark,
  },
  footer: {
    backgroundColor: colors.footer,
    color: 'white',
    padding: '3rem 1.5rem 2rem',
    marginTop: '3rem'
  },
  footerContent: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    marginBottom: '2rem'
  },
  footerSection: {
    textAlign: 'left'
  },
  footerTitle: {
    fontSize: '0.8rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  footerLink: {
    color: 'rgba(255,255,255,0.8)',
    textDecoration: 'none',
    display: 'block',
    marginBottom: '0.6rem',
    transition: 'color 0.2s',
    fontSize: '0.95rem',
  },
  socialLinks: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem'
  },
  socialIcon: {
    color: 'rgba(255,255,255,0.6)',
    textDecoration: 'none',
    fontSize: '1.25rem',
    transition: 'color 0.2s'
  },
  footerBottom: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '1.5rem',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.65)',
    fontSize: '0.85rem',
  },
  successMessage: {
    backgroundColor: colors.success,
    color: 'white',
    padding: '0.85rem 1.5rem',
    borderRadius: '10px',
    marginTop: '1rem',
    display: 'inline-block',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  errorMessage: {
    backgroundColor: colors.error,
    color: 'white',
    padding: '0.85rem 1.5rem',
    borderRadius: '10px',
    marginTop: '1rem',
    display: 'inline-block',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  input: {
    padding: '13px 16px',
    fontSize: '1rem',
    borderRadius: 12,
    border: `1px solid ${colors.borderDark}`,
    fontFamily: fonts.body,
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    backgroundColor: colors.white,
    outline: 'none',
    color: colors.text,
  },
  // Soft tinted "tag" used for role categories, language labels, etc.
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 999,
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.01em',
    lineHeight: 1,
  },
  sectionTitle: {
    fontSize: 'clamp(1.6rem, 4vw, 2.1rem)',
    fontFamily: fonts.heading,
    fontWeight: 800,
    color: colors.text,
    letterSpacing: '-0.01em',
    lineHeight: 1.12,
  }
};

export default styles;
